import { ModuleInfo, NameSectionInfo } from '../BinaryReader';
import { demangleName } from './Demangler';
import { FieldResolver } from './LoweredEmitter';
import { FrameLocals } from './DwarfFrameLocals';
import { FunctionDecompiler } from './FunctionDecompiler';
import type { NameResolver, NameResolution } from './NameResolver';

export type { NameResolver, NameResolution };

export function sanitizeIdentifier(name: string): string {
  return name.replace(/[^a-zA-Z0-9_$]/g, '_');
}

// Emscripten's name section stores already-demangled names that carry the full parameter
// signature, e.g. "geometry::distance_squared(Point const*)". That signature duplicates the
// decompiled parameter list and sanitizes into noise, so drop the trailing argument list.
// Demangling a mangled name produces no signature, so this is a no-op for those.
function stripParameterSignature(name: string): string {
  if (!name.endsWith(')')) {
    return name;
  }
  let depth = 0;
  for (let index = name.length - 1; index >= 0; index--) {
    if (name[index] === ')') {
      depth++;
    } else if (name[index] === '(') {
      depth--;
      if (depth === 0) {
        const head = name.slice(0, index).trimEnd();
        // An empty head or one ending in "operator" means the parens are part of the
        // name itself (operator(), conversion operators), not an argument list.
        if (head.length === 0 || head.endsWith('operator')) {
          return name;
        }
        return head;
      }
    }
  }
  return name;
}

function cleanFunctionName(rawName: string): string {
  return sanitizeIdentifier(stripParameterSignature(demangleName(rawName)));
}

type FunctionNameProvider = (globalIndex: number) => string | null;
type LocalNameProvider = (funcGlobalIndex: number, localIndex: number) => string | null;
type ParameterTypeProvider = (funcGlobalIndex: number, paramIndex: number) => string | null;

function resolveFunctionName(nameSection: NameSectionInfo | undefined, provider: FunctionNameProvider | undefined, globalIndex: number): NameResolution {
  if (nameSection?.functionNames?.has(globalIndex)) {
    return { name: cleanFunctionName(nameSection.functionNames.get(globalIndex)!), source: 'name-section' };
  }
  if (provider) {
    const externalName = provider(globalIndex);
    if (externalName) {
      return { name: cleanFunctionName(externalName), source: 'dwarf' };
    }
  }
  return { name: `func_${globalIndex}`, source: 'generated' };
}

function resolveLocalName(nameSection: NameSectionInfo | undefined, provider: LocalNameProvider | undefined, funcGlobalIndex: number, localIndex: number): NameResolution {
  const sectionLocals = nameSection?.localNames?.get(funcGlobalIndex);
  if (sectionLocals?.has(localIndex)) {
    return { name: sanitizeIdentifier(sectionLocals.get(localIndex)!), source: 'name-section' };
  }
  if (provider) {
    const dwarfName = provider(funcGlobalIndex, localIndex);
    if (dwarfName) {
      return { name: sanitizeIdentifier(dwarfName), source: 'dwarf' };
    }
  }
  return { name: `var${localIndex}`, source: 'generated' };
}

function resolveGlobalName(nameSection: NameSectionInfo | undefined, globalIndex: number): NameResolution {
  if (nameSection?.globalNames?.has(globalIndex)) {
    return { name: sanitizeIdentifier(nameSection.globalNames.get(globalIndex)!), source: 'name-section' };
  }
  return { name: `global_${globalIndex}`, source: 'generated' };
}

export function createNameResolver(
  moduleInfo: ModuleInfo,
  functionNameProvider?: FunctionNameProvider,
  localNameProvider?: LocalNameProvider,
  parameterTypeProvider?: ParameterTypeProvider,
): NameResolver {
  const nameSection = moduleInfo.nameSection;
  return {
    functionName: (globalIndex: number): NameResolution => resolveFunctionName(nameSection, functionNameProvider, globalIndex),
    localName: (funcGlobalIndex: number, localIndex: number): NameResolution => resolveLocalName(nameSection, localNameProvider, funcGlobalIndex, localIndex),
    globalName: (globalIndex: number): NameResolution => resolveGlobalName(nameSection, globalIndex),
    typeName: (): string | null => null,
    parameterType: (funcGlobalIndex: number, paramIndex: number): string | null =>
      parameterTypeProvider ? parameterTypeProvider(funcGlobalIndex, paramIndex) : null,
  };
}

export function decompileFunction(
  moduleInfo: ModuleInfo,
  localFuncIndex: number,
  nameResolver: NameResolver,
  fieldResolver?: FieldResolver,
  frameLocals?: FrameLocals,
): string {
  return new FunctionDecompiler(moduleInfo, localFuncIndex, nameResolver, fieldResolver, frameLocals).decompile();
}
